import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { preloadAudio, unlockAudioContext } from '../../shared/audioManager';
import { useAppSelector } from '../../core/redux/hooks/useAppSelector';
import { selectIsSoundEnabled } from '../../modules/game/selectors';

type TBootstrapConfig = {
  backgroundMusicSrc: string;
};

type TBootstrapProps = {
  config: TBootstrapConfig;
};

const Bootstrap: FC<PropsWithChildren<TBootstrapProps>> = ({ config, children }) => {
  const { backgroundMusicSrc } = config;
  const isSoundEnabled = useAppSelector(selectIsSoundEnabled);

  const bgAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!backgroundMusicSrc) return;

    const audio = new Audio(backgroundMusicSrc);
    audio.loop = true;
    bgAudioRef.current = audio;

    unlockAudioContext();
    preloadAudio(backgroundMusicSrc).catch((err) => {
      console.error('preloadAudio error for bg music:', err);
    });

    return () => {
      audio.pause();
      bgAudioRef.current = null;
    };
  }, [backgroundMusicSrc]);

  useEffect(() => {
    const audio = bgAudioRef.current;
    if (!audio || !backgroundMusicSrc) return;

    if (!isSoundEnabled) {
      audio.pause();
      return;
    }

    let disposed = false;

    const tryPlay = async () => {
      if (disposed) return;
      const current = bgAudioRef.current;
      if (!current) return;

      try {
        await unlockAudioContext();
        await preloadAudio(backgroundMusicSrc);
        await current.play();
        removeListeners();
      } catch (err) {
        console.warn('Background music play blocked, waiting for user gesture', err);
      }
    };

    const onUserGesture = () => {
      void tryPlay();
    };

    const addListeners = () => {
      window.addEventListener('pointerdown', onUserGesture);
      window.addEventListener('touchstart', onUserGesture);
      window.addEventListener('click', onUserGesture);
    };

    const removeListeners = () => {
      window.removeEventListener('pointerdown', onUserGesture);
      window.removeEventListener('touchstart', onUserGesture);
      window.removeEventListener('click', onUserGesture);
    };

    addListeners();
    tryPlay(); 

    return () => {
      disposed = true;
      removeListeners();
    };
  }, [isSoundEnabled, backgroundMusicSrc]);

  return <>{children}</>;
};

export default Bootstrap;
